import { captchaMiddleware } from '@/middlewares/captcha/captchaMiddleware';
import { isSessionExpiredModalOpen } from '@/contexts/SessionExpired/utils';

import { antiFloodMiddleware } from '../../middlewares/auth/AntiFloodMiddleware';
import { formatMiddleware } from '../../middlewares/auth/FormatMiddleware';
import { availabiltyMiddleware } from '../../middlewares/AvailabiltyMiddleware';
import { updateDataMiddleware } from '../../middlewares/UpdateDataMiddleware';

import { request } from './request';

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

export interface RequestOptions {
  headers?: Headers;
}

export type ClientFactory = (
  guard: (cb: (headers?: Headers) => ReturnType<typeof request>, headers?: Headers) => Promise<Response>,
  customBaseUrl?: string,
) => {
  get: <T>(url: string, options?: RequestOptions, apiVersion?: 'v1' | 'v2') => Promise<T>;
  post: <T>(url: string, data?: unknown, headers?: Headers) => Promise<T>;
  patch: <T>(url: string, data?: unknown, headers?: Headers) => Promise<T>;
  put: <T>(url: string, data?: unknown, headers?: Headers) => Promise<T>;
  delete: (url: string, data?: unknown) => Promise<void>;
};

export const clientFactory: ClientFactory = (guard, customBaseUrl) => ({
  get: async (url, options = { headers: new Headers() }, apiVersion = 'v1') => {
    if (isSessionExpiredModalOpen()) throw new SessionExpiredError();

    return formatMiddleware(
      await antiFloodMiddleware(
        await availabiltyMiddleware(
          await captchaMiddleware(
            async (resultHeaders) =>
              await guard(
                (headers) => request(url, 'GET', null, headers, {}, apiVersion, customBaseUrl),
                resultHeaders,
              ),
            url,
            options.headers,
          ),
        ),
      ),
      options.headers,
    );
  },
  post: async (url, data = null, headers = new Headers()) => {
    if (isSessionExpiredModalOpen()) throw new SessionExpiredError();

    return formatMiddleware(
      await updateDataMiddleware(
        await antiFloodMiddleware(
          await availabiltyMiddleware(
            await captchaMiddleware(
              async (resultHeaders) =>
                await guard((headers) => request(url, 'POST', data, headers, {}, 'v1', customBaseUrl), resultHeaders),
              url,
              headers,
            ),
          ),
        ),
      ),
      headers,
    );
  },
  patch: async (url, data = null, headers = new Headers()) => {
    if (isSessionExpiredModalOpen()) throw new SessionExpiredError();

    return formatMiddleware(
      await updateDataMiddleware(
        await antiFloodMiddleware(
          await availabiltyMiddleware(
            await captchaMiddleware(
              async (resultHeaders) =>
                await guard((headers) => request(url, 'PATCH', data, headers, {}, 'v1', customBaseUrl), resultHeaders),
              url,
              headers,
            ),
          ),
        ),
      ),
      headers,
    );
  },
  put: async (url, data = null, headers = new Headers()) => {
    if (isSessionExpiredModalOpen()) throw new SessionExpiredError();

    return formatMiddleware(
      await updateDataMiddleware(
        await antiFloodMiddleware(
          await availabiltyMiddleware(
            await captchaMiddleware(
              async (resultHeaders) =>
                await guard((headers) => request(url, 'PUT', data, headers, {}, 'v1', customBaseUrl), resultHeaders),
              url,
              headers,
            ),
          ),
        ),
      ),
      headers,
    );
  },
  delete: async (url, data = null) => {
    if (isSessionExpiredModalOpen()) throw new SessionExpiredError();

    return formatMiddleware(
      await updateDataMiddleware(
        await antiFloodMiddleware(
          await availabiltyMiddleware(
            await captchaMiddleware(
              async (resultHeaders) =>
                await guard((headers) => request(url, 'DELETE', data, headers, {}, 'v1', customBaseUrl), resultHeaders),
              url,
              undefined,
            ),
          ),
        ),
      ),
    );
  },
});
